export type DepthQuote = {
    price?: number | null
    quantity?: number | null
    numberOfOrders?: number | null
}

/** Placeholder book level (0 price / 0 qty) — still shown as 0.00 in the UI. */
export const isEmptyDepthLevel = (quote?: DepthQuote | null) =>
    !!quote &&
    (quote.price == null || quote.price === 0) &&
    (quote.quantity ?? 0) === 0 &&
    (quote.numberOfOrders ?? 0) === 0

export type AlignedDepthSlot = {
    /** Index into the original side array. -1 when this side has no level on the row. */
    index: number
    market: boolean
}

export type AlignedDepthRow = {
    bid: AlignedDepthSlot
    ask: AlignedDepthSlot
}

const EMPTY_SLOT: AlignedDepthSlot = { index: -1, market: false }

export const isMarketOrderQuote = (quote?: DepthQuote | null) =>
    !!quote && (quote.price == null || quote.price === 0) && (quote.quantity ?? 0) > 0

const splitQuoteIndices = (quotes: DepthQuote[] | undefined) => {
    const limit: number[] = []
    const market: number[] = []
    if (!quotes) return { limit, market }
    for (let i = 0; i < quotes.length; i++) {
        const quote = quotes[i]
        if (isEmptyDepthLevel(quote)) limit.push(i)
        else if (isMarketOrderQuote(quote)) market.push(i)
        else limit.push(i)
    }
    return { limit, market }
}

/**
 * Limit levels stay in book order. Market orders are moved to the bottom and
 * aligned so a market order on both sides shares the same last row.
 */
export const alignDepthRows = (
    bids: DepthQuote[] | undefined,
    asks: DepthQuote[] | undefined
): AlignedDepthRow[] => {
    const bid = splitQuoteIndices(bids)
    const ask = splitQuoteIndices(asks)
    const limitCount = Math.max(bid.limit.length, ask.limit.length)
    const marketCount = Math.max(bid.market.length, ask.market.length)
    const rows: AlignedDepthRow[] = []

    for (let i = 0; i < limitCount; i++) {
        rows.push({
            bid: bid.limit[i] != null ? { index: bid.limit[i], market: false } : EMPTY_SLOT,
            ask: ask.limit[i] != null ? { index: ask.limit[i], market: false } : EMPTY_SLOT,
        })
    }

    const bidPad = marketCount - bid.market.length
    const askPad = marketCount - ask.market.length
    for (let i = 0; i < marketCount; i++) {
        const bidIdx = i - bidPad
        const askIdx = i - askPad
        rows.push({
            bid: bidIdx >= 0 ? { index: bid.market[bidIdx], market: true } : EMPTY_SLOT,
            ask: askIdx >= 0 ? { index: ask.market[askIdx], market: true } : EMPTY_SLOT,
        })
    }

    return rows
}

/** Row count for paired tables — matches Android `max(bids, asks)` with empty-book fallback. */
export const depthBodyRowCount = (
    bids: DepthQuote[] | undefined,
    asks: DepthQuote[] | undefined,
    alignedRowCount: number,
    emptyFallback = 5
) => {
    const dataRows = Math.max(bids?.length ?? 0, asks?.length ?? 0, alignedRowCount)
    return dataRows > 0 ? dataRows : emptyFallback
}

export const depthRowsEqual = (a: AlignedDepthRow[], b: AlignedDepthRow[]) => {
    if (a === b) return true
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) {
        if (a[i].bid.index !== b[i].bid.index || a[i].bid.market !== b[i].bid.market) return false
        if (a[i].ask.index !== b[i].ask.index || a[i].ask.market !== b[i].ask.market) return false
    }
    return true
}

/** One "Market" label on a shared row: the bid price cell when both sides are market. */
export const showBidMarketLabel = (row: AlignedDepthRow) => row.bid.market
export const showAskMarketLabel = (row: AlignedDepthRow) => row.ask.market && !row.bid.market
